import { Category } from '@src/main/modules/parameters/domain/issues/category.interface';
import { CategoryRepository } from '@src/main/modules/parameters/domain/issues/category.repository';
import {
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '@src/main/modules/parameters/infrastructure/dto/createUpdateCategoryRequest';
import {
  CategoryEntity,
  toCategory,
} from '@src/main/modules/parameters/infrastructure/entity/category.entity';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';

const categoriesPath = '/categories';

export const createCategoryHttpRepository = (tokenProvider: TokenProvider): CategoryRepository =>
  new CategoryHttpRepository(tokenProvider);

class CategoryHttpRepository implements CategoryRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findAll(): Promise<Category[]> {
    const token = await this.getAuthToken();
    const response = await api.get<CategoryEntity[]>(`${categoriesPath}/all`, token);
    return response.map(toCategory);
  }

  async findById(id: string): Promise<Category> {
    const token = await this.getAuthToken();
    const response = await api.get<CategoryEntity>(`${categoriesPath}/${id}`, token);
    return toCategory(response);
  }

  async create(category: CreateCategoryRequest): Promise<Category> {
    const token = await this.getAuthToken();
    const response = await api.post<CategoryEntity>(categoriesPath, token, category);
    return toCategory(response);
  }

  async update(id: string, category: UpdateCategoryRequest): Promise<Category> {
    const token = await this.getAuthToken();
    const response = await api.put<CategoryEntity>(`${categoriesPath}/${id}`, token, category);
    return toCategory(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${categoriesPath}/${id}`, token);
  }
}
